import _ from 'lodash';
import { defineStore } from 'pinia';

import TableReservationApi from '@/extensions/tableReservation/store/api/tableReservation';
import { buildSettingsInput, normalizeSettings } from '@/extensions/tableReservation/store/utils';
import { useSetupStore } from '@/stores/setup';

const defaultSettings = {
    defaultDuration: 90,
    tablePlacementMode: 'manual',
    touchKeyboardAutoOpen: false,
    presetButtonSize: 'default',
    noteTags: [],
    onlineEnabled: false,
    serviceTimes: [],
    closedDates: [],
    slotInterval: 30,
    leadTime: 120,
    maxAdvanceDays: 90,
    minPartySize: 1,
    maxPartySize: 10,
    autoConfirm: true,
    guestConfirmationEnabled: true,
    notificationRecipients: [],
    onlineHint: ''
};

let savedResetTimeout = null;

export const durationOptions = [
    { value: 30, label: '30 Minuten' },
    { value: 60, label: '1 Stunde' },
    { value: 90, label: '1,5 Stunden' },
    { value: 120, label: '2 Stunden' },
    { value: 150, label: '2,5 Stunden' },
    { value: 180, label: '3 Stunden' }
];

export const reservationTimeWindow = {
    start: '08:00',
    step: '00:15',
    end: '23:30',
    timelineSlotMinutes: 30
};

export const presetButtonSizeOptions = [
    { value: 'default', label: 'Standard' },
    { value: 'large', label: 'Groß' },
    { value: 'xlarge', label: 'Sehr groß' }
];

export const slotIntervalOptions = [
    { value: 15, label: '15 Minuten' },
    { value: 30, label: '30 Minuten' },
    { value: 60, label: '60 Minuten' }
];

export const leadTimeOptions = [
    { value: 0, label: 'Ohne Vorlauf' },
    { value: 60, label: '1 Stunde' },
    { value: 120, label: '2 Stunden' },
    { value: 240, label: '4 Stunden' },
    { value: 1440, label: '1 Tag' }
];

export const weekdayOptions = [
    { value: 1, label: 'Montag', short: 'Mo' },
    { value: 2, label: 'Dienstag', short: 'Di' },
    { value: 3, label: 'Mittwoch', short: 'Mi' },
    { value: 4, label: 'Donnerstag', short: 'Do' },
    { value: 5, label: 'Freitag', short: 'Fr' },
    { value: 6, label: 'Samstag', short: 'Sa' },
    { value: 7, label: 'Sonntag', short: 'So' }
];

export const tablePlacementModeOptions = [
    { value: 'manual', label: 'Manuell' },
    { value: 'automatic', label: 'Automatisch' }
];

export const useTableReservationSettingsStore = defineStore('tableReservationSettings', {
    state: function () {
        return {
            loading: false,
            settings: _.cloneDeep(defaultSettings),
            saved: false
        };
    },

    getters: {},

    actions: {
        hydrateSettings: function (settings) {
            this.settings = _.assign({}, _.cloneDeep(defaultSettings), normalizeSettings(settings || {}));
            this.saved = false;
        },

        updateSettings: function (data) {
            this.settings = _.assign({}, this.settings, data);
            this.saved = false;
        },

        saveSettings: async function () {
            const cmsId = _.get(useSetupStore(), 'activeCms.id');
            if (!cmsId) {
                throw new Error('No active CMS selected');
            }

            this.loading = true;

            try {
                const settings = await TableReservationApi.updateSettings(
                    buildSettingsInput(this.settings, cmsId)
                );

                this.hydrateSettings(settings);
                this.saved = true;

                if (savedResetTimeout) {
                    clearTimeout(savedResetTimeout);
                }

                savedResetTimeout = setTimeout(() => {
                    this.saved = false;
                }, 2000);

                return this.settings;
            } finally {
                this.loading = false;
            }
        },

        reset: function () {
            this.settings = _.cloneDeep(defaultSettings);
            this.saved = false;
        }
    }
});
